// ============================================================
// NarrativeEntry - one line of the classic combat log: an attack's
// jam, miss or hit (with every hit's damage arithmetic), or a plain
// info line. The collapsible view is `NarrativeEntryStructured`.
//
// Each outcome is its own small component so the entry reads as a
// dispatch; the status sentence under a hit is shared with the
// structured view through `engine/statusLineText`.
// ============================================================
import { memo } from 'react';
import common from '../styles/common.module.css';
import { DAMAGE_TYPE_COLORS } from '../engine/constants';
import { formatDiceResult } from '../engine/structuredNarrative';
import { magnitudeSummary, woundsSummary } from '../engine/statusLineText';
import { useWoundDisplay } from '../hooks/useWoundDisplay';

const TARGET_STYLE = { color: 'var(--accent)', fontSize: 9, marginRight: 4 };
const NOTE_STYLE = { marginLeft: 12, fontSize: 10, color: 'var(--arcane)' };

/** "→ Ork Nob", when the entry names who it was aimed at. */
function TargetTag({ name }) {
  return (
    <span style={TARGET_STYLE}>
      {'→'} {name}
    </span>
  );
}

function SpecialNotes({ notes }) {
  return (
    notes &&
    notes.map((n, ni) => (
      <div key={ni} style={NOTE_STYLE}>
        {n}
      </div>
    ))
  );
}

function JamEntry({ entry }) {
  const { result, slotName } = entry;
  return (
    <div className={common.logEntry} style={{ borderLeft: '3px solid var(--warn)' }}>
      {entry.targetName && <TargetTag name={entry.targetName} />}
      <b style={{ color: 'var(--warn)' }}>JAM!</b> {slotName} jams (rolled {result.attackRoll}).
      {result.overheatDmg ? ' Overheats: ' + result.overheatDmg + ' E to wielder!' : ''}
    </div>
  );
}

function MissEntry({ entry }) {
  const { result, slotName, fireMode } = entry;
  return (
    <div className={common.logEntry} style={{ borderLeft: '3px solid var(--neutral)' }}>
      {entry.targetName && <TargetTag name={entry.targetName} />}
      <b style={{ color: 'var(--danger)' }}>MISS</b> {slotName} ({fireMode}). Rolled {result.attackRoll} vs{' '}
      {result.modifiedSkill}. {result.dof} DoF.
      <SpecialNotes notes={result.specialNotes} />
    </div>
  );
}

/** A dodge or parry: the roll, and how many hits it took off. */
function DefenceLine({ label, verb, color, roll }) {
  return (
    <div style={{ marginLeft: 12, color, fontSize: 11 }}>
      {label}: {roll.roll} vs {roll.skill} {roll.success ? '- ' + verb + ' ' + roll.avoided + ' hit(s)' : '- failed!'}
    </div>
  );
}

function OverloadNote({ h }) {
  if (h.ffOverloaded) {
    return (
      <span style={{ color: 'var(--warn)', fontWeight: 'bold', marginLeft: 4 }}>
        OVERLOADED! (≤{h.ffOverloadThreshold})
      </span>
    );
  }
  return h.ffOverloadThreshold > 0 ? (
    <span style={{ color: 'var(--text-dim)', marginLeft: 4 }}>(overload ≤{h.ffOverloadThreshold})</span>
  ) : null;
}

function ForceFieldBlocked({ h }) {
  return (
    <span style={{ color: 'var(--info)' }}>
      FORCE FIELD blocked (d100={h.ffRoll} vs {h.ffProtection})
      <OverloadNote h={h} />
    </span>
  );
}

function RighteousFury({ h }) {
  return (
    <span style={{ color: h.rfConfirmed ? 'var(--warn)' : 'var(--border-bright)', marginLeft: 4 }}>
      RF {h.rfConfirmed ? 'CONFIRMED sev ' + h.rfSeverity : 'unconfirmed'}
      {h.rfAutoConfirmed ? ' (Deathwatch Training vs Xenos)' : ''}
    </span>
  );
}

/** A hit that got through the field: dice, armour, Toughness, and what it came to. */
function HitDamage({ h }) {
  return (
    <>
      <span style={{ color: 'var(--text-dim)' }}>{h.dmgRoll ? formatDiceResult(h.dmgRoll) : ''}</span> = {h.rawDamage}{' '}
      raw - {h.effectiveAP} AP
      {h.coverAP > 0 && <span style={{ color: 'var(--info)' }}> (incl. {h.coverAP} cover)</span>} - {h.tb} TB ={' '}
      <b style={{ color: h.effectiveDamage > 0 ? 'var(--danger)' : 'var(--neutral)' }}>{h.effectiveDamage}</b>
      {h.ffRoll > 0 && (
        <span style={{ color: 'var(--info)', marginLeft: 4, fontSize: 10 }}>
          [FF d100={h.ffRoll} vs {h.ffProtection} - passed]
        </span>
      )}
      {h.effectiveDamage === 0 && ' (absorbed)'}
      {h.righteousFury && <RighteousFury h={h} />}
    </>
  );
}

const CRIT_STYLE = {
  marginLeft: 8,
  marginTop: 2,
  padding: '3px 6px',
  background: 'var(--danger-ground)',
  borderRadius: 3,
  border: '1px solid var(--danger-deep)',
  fontSize: 10,
};

function CritBox({ h }) {
  return (
    <div style={CRIT_STYLE}>
      <span style={{ color: 'var(--danger-soft)', fontWeight: 'bold' }}>CRITICAL (Sev {h.critSev})</span>:{' '}
      <span style={{ color: 'var(--text-primary)' }}>{h.crit.text}</span>
      {h.crit.isDead && (
        <span style={{ color: 'var(--danger-strong)', fontWeight: 'bold', marginLeft: 4 }}>LETHAL</span>
      )}
    </div>
  );
}

function HitLine({ h }) {
  return (
    <div style={{ marginLeft: 12, marginTop: 3, fontSize: 11, color: 'var(--neutral-bright)' }}>
      <span style={{ color: DAMAGE_TYPE_COLORS[h.damageType]?.bg }}>{h.location.name}</span>:{' '}
      {h.ffBlocked ? <ForceFieldBlocked h={h} /> : <HitDamage h={h} />}
      {h.crit && <CritBox h={h} />}
    </div>
  );
}

function HordeTotal({ entry, showNumbers }) {
  const destroyed = entry.magAfter <= 0;
  const tally = { dealt: entry.magDmg, after: entry.magAfter, max: entry.magMax, destroyed };
  return (
    <div style={{ marginTop: 4, fontWeight: 'bold', color: destroyed ? 'var(--danger-strong)' : 'var(--warn)' }}>
      {magnitudeSummary(tally, showNumbers)}
    </div>
  );
}

function WoundsTotal({ entry, showNumbers }) {
  const tally = { dealt: entry.totalDamage, after: entry.woundsAfter, max: entry.woundsMax };
  return (
    <div
      style={{
        marginTop: 4,
        fontWeight: 'bold',
        color: entry.woundsAfter < 0 ? 'var(--danger-strong)' : 'var(--danger)',
      }}
    >
      {woundsSummary(tally, showNumbers)}
    </div>
  );
}

/** The status sentence, only when the attack actually took something. */
function EntryTotal({ entry, showNumbers }) {
  if (entry.isHorde) return entry.magDmg > 0 && <HordeTotal entry={entry} showNumbers={showNumbers} />;
  return entry.totalDamage > 0 && <WoundsTotal entry={entry} showNumbers={showNumbers} />;
}

function HitEntry({ entry }) {
  const { showNumbers } = useWoundDisplay();
  const { result, slotName, fireMode } = entry;
  return (
    <div className={common.logEntry} style={{ borderLeft: '3px solid var(--ok)' }}>
      <div>
        {entry.targetName && <TargetTag name={entry.targetName} />}
        <b style={{ color: 'var(--ok)' }}>HIT!</b> {slotName} ({fireMode}). Rolled {result.attackRoll} vs{' '}
        {result.modifiedSkill}. {result.dos} DoS = {result.totalHits} hit{result.totalHits !== 1 ? 's' : ''}.
      </div>
      <SpecialNotes notes={result.specialNotes} />
      {result.dodgeResult && <DefenceLine label="Dodge" verb="avoided" color="var(--info)" roll={result.dodgeResult} />}
      {result.parryResult && (
        <DefenceLine label="Parry" verb="deflected" color="var(--arcane)" roll={result.parryResult} />
      )}
      {result.hitsAfterDefence.map((h, i) => (
        <HitLine key={i} h={h} />
      ))}
      <EntryTotal entry={entry} showNumbers={showNumbers} />
    </div>
  );
}

function AttackResultEntry({ entry }) {
  if (entry.result.jam) return <JamEntry entry={entry} />;
  if (!entry.result.success) return <MissEntry entry={entry} />;
  return <HitEntry entry={entry} />;
}

function InfoEntry({ entry }) {
  return (
    <div className={common.logEntry} style={{ borderLeft: '3px solid var(--info-strong)', color: 'var(--info-soft)' }}>
      {entry.targetName && <TargetTag name={entry.targetName} />}
      {entry.text}
    </div>
  );
}

function NarrativeEntry({ entry }) {
  if (entry.type === 'attack-result') return <AttackResultEntry entry={entry} />;
  if (entry.type === 'info') return <InfoEntry entry={entry} />;
  return null;
}

export default memo(NarrativeEntry);
